import { Concern, Room, UnitSystem, parsePositive, validateRoom } from './calculator';
import {
  Preferences, DEFAULT_PREFS, validatePrefs, Country, IonizerPref, SortKey, GasSource, GasPersistence, FILTER_FACTORS,
  GAS_SOURCE_LABELS,
} from './matching';

export const SHARE_VERSION = '3';

export interface ShareState {
  rooms: Room[];
  names: string[];
  units: UnitSystem;
  prefs: Preferences;
}

const CONCERNS = new Set<Concern>(['general', 'allergies', 'pets', 'dust', 'wildfire', 'gases']);
const SORTS = new Set<SortKey>(['three-year', 'purchase', 'output', 'noise']);
const IONS = new Set<IonizerPref>(['any', 'off-or-none', 'none']);
const PERSIST = new Set<GasPersistence>(['occasional', 'ongoing']);

export function encodeShare(s: ShareState): URLSearchParams {
  const p = new URLSearchParams();
  const pr = s.prefs;
  p.set('v', SHARE_VERSION);
  p.set('rooms', s.rooms.map((r) => `${+r.areaSqFt.toFixed(4)}x${+r.ceilingFt.toFixed(4)}`).join(';'));
  p.set('names', s.names.map((n) => n.replace(/[;]/g, ' ').slice(0, 40)).join(';'));
  p.set('u', s.units);
  p.set('country', pr.country);
  p.set('c', pr.concerns.join(','));
  if (pr.customEach != null) p.set('each', String(pr.customEach));
  if (pr.maxNoiseDbA != null) p.set('noise', String(pr.maxNoiseDbA));
  if (pr.maxPurchaseUsd != null) p.set('buy', String(pr.maxPurchaseUsd));
  if (pr.maxAnnualUsd != null) p.set('yr', String(pr.maxAnnualUsd));
  p.set('two', pr.allowTwoUnits ? '1' : '0');
  if (pr.diyOnly) p.set('diy', '1');
  p.set('ion', pr.ionizer);
  p.set('hrs', String(pr.costs.hoursPerDay));
  p.set('kwh', String(pr.costs.tariffUsdPerKwh));
  p.set('ff', String(pr.costs.filterIntervalFactor));
  p.set('sort', pr.sort);
  if (pr.gasSource) p.set('gs', pr.gasSource);
  if (pr.gasPersistence) p.set('gp', pr.gasPersistence);
  return p;
}

export interface DecodeResult {
  state: ShareState | null;
  legacy: boolean;
  /** Link was present but could not be used */
  error: string | null;
  warnings: string[];
}

export function decodeShare(p: URLSearchParams): DecodeResult {
  const warnings: string[] = [];
  const v = p.get('v');
  if (v != null && v !== SHARE_VERSION)
    return { state: null, legacy: false, error: `This link uses an unsupported format (version ${v.slice(0, 8)}). Please re-enter your room.`, warnings };

  if (v === SHARE_VERSION) {
    const rooms: Room[] = [];
    const rawNames = (p.get('names') || '').split(';');
    const names: string[] = [];
    (p.get('rooms') || '').split(';').filter(Boolean).slice(0, 10).forEach((part, i) => {
      const [a, h] = part.split('x').map((x) => parsePositive(x));
      if (a == null || h == null || !validateRoom({ areaSqFt: a, ceilingFt: h }).ok) {
        warnings.push(`Room ${i + 1} in this link had invalid measurements and was skipped.`);
        return;
      }
      rooms.push({ areaSqFt: a, ceilingFt: h });
      names.push((rawNames[i] || `Room ${rooms.length}`).slice(0, 40));
    });
    const gasOnlyLink = (p.get('c') || '') === 'gases';
    if (rooms.length === 0 && !gasOnlyLink) return { state: null, legacy: false, error: 'This link has no valid room. Please re-enter your room.', warnings };

    const prefs: Preferences = { ...DEFAULT_PREFS, costs: { ...DEFAULT_PREFS.costs } };
    const country = p.get('country');
    if (country === 'US' || country === 'other') prefs.country = country as Country;
    else { prefs.country = 'other'; warnings.push('Country missing or invalid in this link — showing sizing only, without US product results. Choose a country to see products.'); }

    const cs = (p.get('c') || '').split(',').filter(Boolean);
    const valid = cs.filter((c): c is Concern => CONCERNS.has(c as Concern));
    if (valid.length !== cs.length) warnings.push('Some concerns in this link were not recognised and were ignored.');
    prefs.concerns = valid.length ? valid : ['general'];

    const num = (key: string, label: string): number | null => {
      const raw = p.get(key);
      if (raw == null) return null;
      const n = parsePositive(raw);
      if (n == null) warnings.push(`${label} in this link was invalid and was ignored.`);
      return n;
    };
    prefs.customEach = num('each', 'Custom air changes');
    prefs.maxNoiseDbA = num('noise', 'Noise limit');
    prefs.maxPurchaseUsd = num('buy', 'Purchase budget');
    prefs.maxAnnualUsd = num('yr', 'Yearly budget');
    prefs.allowTwoUnits = p.get('two') !== '0';
    prefs.diyOnly = p.get('diy') === '1';
    const ion = p.get('ion');
    if (ion && IONS.has(ion as IonizerPref)) prefs.ionizer = ion as IonizerPref;
    else if (ion) warnings.push('Ionizer preference in this link was invalid; using "no preference".');
    const hrs = num('hrs', 'Hours per day'); if (hrs != null) prefs.costs.hoursPerDay = hrs;
    const kwh = num('kwh', 'Electricity price'); if (kwh != null) prefs.costs.tariffUsdPerKwh = kwh;
    const ff = p.get('ff');
    if (ff != null) {
      const n = Number(ff);
      if ((FILTER_FACTORS as readonly number[]).includes(n)) prefs.costs.filterIntervalFactor = n;
      else warnings.push('Filter-life setting in this link was invalid; using the manufacturer interval.');
    }
    const sort = p.get('sort');
    if (sort && SORTS.has(sort as SortKey)) prefs.sort = sort as SortKey;
    else if (sort) warnings.push('Sort option in this link was invalid; using 3-year cost.');
    const gs = p.get('gs');
    if (gs && gs in GAS_SOURCE_LABELS) prefs.gasSource = gs as GasSource; else if (gs) warnings.push('Gas source in this link was not recognised.');
    const gp = p.get('gp');
    if (gp && PERSIST.has(gp as GasPersistence)) prefs.gasPersistence = gp as GasPersistence;

    // Out-of-range numeric preferences: reset that field with a warning
    const errs = validatePrefs(prefs);
    if (errs.each) { prefs.customEach = null; warnings.push('Custom air changes out of range; using the default target.'); }
    if (errs.noise) { prefs.maxNoiseDbA = null; warnings.push('Noise limit out of range and ignored.'); }
    if (errs.purchase) { prefs.maxPurchaseUsd = null; warnings.push('Purchase budget out of range and ignored.'); }
    if (errs.annual) { prefs.maxAnnualUsd = null; warnings.push('Yearly budget out of range and ignored.'); }
    if (errs.hours) { prefs.costs.hoursPerDay = DEFAULT_PREFS.costs.hoursPerDay; warnings.push('Hours per day out of range; using the default.'); }
    if (errs.tariff) { prefs.costs.tariffUsdPerKwh = DEFAULT_PREFS.costs.tariffUsdPerKwh; warnings.push('Electricity price out of range; using the default.'); }

    return { state: { rooms, names, units: p.get('u') === 'metric' ? 'metric' : 'imperial', prefs }, legacy: false, error: null, warnings };
  }

  // Legacy unversioned links: old app always stored ft² and ft, even when labelled metric.
  if (p.has('sqft') || p.has('h')) {
    const sqft = parsePositive(p.get('sqft'));
    const h = parsePositive(p.get('h'));
    if (sqft == null || h == null || !validateRoom({ areaSqFt: sqft, ceilingFt: h }).ok)
      return { state: null, legacy: true, error: 'This older link had invalid measurements. Please re-enter your room.', warnings };
    const old = (p.get('concerns') || '').split(',');
    const concerns = new Set<Concern>();
    for (const c of old) {
      if (c === 'smoke') { concerns.add('wildfire'); concerns.add('gases'); }
      else if (CONCERNS.has(c as Concern)) concerns.add(c as Concern);
    }
    warnings.push('This is an older link. Its measurements were read as square feet and feet (how the old version stored them) and recalculated with the current method. Country was not recorded, so only sizing is shown until you choose one.');
    if (old.includes('smoke')) warnings.push('"Smoke & odours" from the old version is now split into smoke particles and gases/odours.');
    return {
      state: {
        rooms: [{ areaSqFt: sqft, ceilingFt: h }], names: ['Room 1'],
        units: p.get('units') === 'metric' ? 'metric' : 'imperial',
        prefs: { ...DEFAULT_PREFS, country: 'other', concerns: concerns.size ? [...concerns] : ['general'] },
      },
      legacy: true, error: null, warnings,
    };
  }
  return { state: null, legacy: false, error: null, warnings };
}
